import * as React from 'react';
import { useLoadingStates } from './use-loading.hook';
import { useAccountStore, useWelcomePackStore } from 'store';
import { WelcomePackUtils } from 'pages/private/devices/welcome-pack/utils';
import { isNullOrEmpty } from 'common-utils';
import { usePromiseMemo } from 'utils/common-utils';
import { notification } from 'antd';
import { WelcomePackDto } from 'dto/welcome-pack.dto';

interface IWelcomePackCommissioningOptions {
    accountId?: number;
    onWelcomePackChange?: (welcomePack: WelcomePackDto | undefined) => void;
}
export const useWelcomePackCommissioning = (options: IWelcomePackCommissioningOptions) => {
    const { accountId } = options;
    const welcomePackStore = useWelcomePackStore();
    const accountStore = useAccountStore();

    const wpLoadingStates = useLoadingStates();
    const allWelcomePacks = usePromiseMemo([], async () => {
        const account = accountStore.getAccountById(accountId);
        if (!account) {
            return [];
        }
        const welcomePacks = await wpLoadingStates.withLoading(async () => {
            return welcomePackStore.getWelcomePacks(account.id, account.uuid);
        });
        return welcomePacks.getOrThrow();
    }, [accountId]);

    const activeWelcomePacks = React.useMemo(() => allWelcomePacks.filter((item) => WelcomePackUtils.isActive(item)) ?? [], [allWelcomePacks]);

    const latestActiveWelcomePack = React.useMemo(() => {
        if (isNullOrEmpty(activeWelcomePacks)) {
            return undefined;
        }
        if (activeWelcomePacks.length > 1) {
            notification.warning({
                message: 'Multiple welcome packs are configured. ALM will automatically select the last one.',
            });
        }
        return activeWelcomePacks[0];
    }, [activeWelcomePacks]);

    const [welcomePack, setWelcomePack] = React.useState<WelcomePackDto>(null);

    React.useEffect(() => {
        if (isNullOrEmpty(latestActiveWelcomePack)) {
            options?.onWelcomePackChange?.(undefined);
            setWelcomePack(undefined);
        }
    }, [latestActiveWelcomePack]);

    const onUpdate = React.useCallback((welcomePack: WelcomePackDto) => {
        setWelcomePack(welcomePack);
        options?.onWelcomePackChange?.(welcomePack);
    }, []);

    return {
        loading: wpLoadingStates.loading,
        allWelcomePacks,
        activeWelcomePacks,
        latestActiveWelcomePack,
        selectedWelcomePack: welcomePack,
        setSelectedWelcomePack: onUpdate,
    };
}